// Lista de archivos: tabla (server-side, con selección entre páginas) en
// pantallas anchas y tarjetas bajo `md`. La data llega ya paginada del servidor.
import React, { useMemo, useState } from "react";
import { Button, Checkbox, Empty, Pagination, Skeleton, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { DeleteOutlined, DownOutlined, EyeOutlined, UpOutlined } from "@ant-design/icons";
import { paginacionServidor } from "../../../utils/tablaPaginacion";
import type {
  Archivo,
  DireccionOrden,
  OrdenArchivos,
} from "../../../services/almacenamiento.api";
import { aperturaDeArchivo, textoAccionAbrir, type Apertura } from "../aperturaArchivo";
import { ayudaPeso, etiquetaFecha, etiquetaTipoArchivo, formatPeso } from "../almacenamientoUtils";
import ArchivoDetalle from "./ArchivoDetalle";
import ArchivoMiniatura from "./ArchivoMiniatura";
import DuenoCell from "./DuenoCell";
import EstadoTag from "./EstadoTag";
import SubidoPorCell from "./SubidoPorCell";

const { Text } = Typography;

export interface ArchivosListaProps {
  archivos: Archivo[];
  cargando: boolean;
  esMovil: boolean;
  page: number;
  size: number;
  total: number;
  onPaginar: (pagina: number, tamano: number) => void;
  seleccion: ReadonlyMap<string, Archivo>;
  onAlternar: (filas: Archivo[], marcar: boolean) => void;
  orden: OrdenArchivos;
  dir: DireccionOrden;
  onOrdenar: (orden: OrdenArchivos, dir: DireccionOrden) => void;
  onEliminarUno: (archivo: Archivo) => void;
  /** Navega a la ruta del módulo dueño del archivo (botón «Abrir» de cada uso, en el detalle). */
  onAbrir: (ruta: string) => void;
  /**
   * Abre el ARCHIVO en sí (round 2026-10-28): imagen / video en un visor, PDF en una pestaña,
   * Office y demás se descargan. Lo disparan el clic en el nombre o la miniatura, el botón
   * «Abrir» y Enter sobre la fila.
   */
  onAbrirArchivo: (archivo: Archivo) => void;
}

/** Ancho de las casillas de expandir y de seleccionar (en la tabla de escritorio). */
const COL_CASILLA = 40;
/** 2 casillas + miniatura 72 + nombre 180 + peso 84 + fecha 152 + dueño 240 + autor 140 + estado 96 + acciones 68. */
export const ANCHO_TABLA = 2 * COL_CASILLA + 72 + 180 + 84 + 152 + 240 + 140 + 96 + 68;

const FechaCell: React.FC<{ archivo: Archivo }> = ({ archivo }) => {
  const f = etiquetaFecha(archivo.fecha, archivo.fechaOrigen);
  const contenido = (
    <Text type={f.aproximada ? "secondary" : undefined} className="alm-fecha">
      {f.texto}
    </Text>
  );
  return f.explicacion ? <Tooltip title={f.explicacion}>{contenido}</Tooltip> : contenido;
};

const PesoCell: React.FC<{ archivo: Archivo }> = ({ archivo }) => (
  <Tooltip title={ayudaPeso(archivo)}>
    <Text strong className="alm-peso">
      {formatPeso(archivo.pesoBytes)}
    </Text>
  </Tooltip>
);

/** Botón «Abrir» del archivo: deshabilitado (con el motivo en el tooltip) si no hay nada que abrir. */
const BotonAbrirArchivo: React.FC<{
  archivo: Archivo;
  apertura: Apertura;
  onAbrirArchivo: (archivo: Archivo) => void;
  /** true = botón de la tarjeta móvil (con texto y alto táctil); false = ícono de la tabla. */
  tarjeta?: boolean;
}> = ({ archivo, apertura, onAbrirArchivo, tarjeta = false }) => {
  const abrible = apertura.modo !== "ninguno";
  const boton = (
    <Button
      className={tarjeta ? "alm-btn-tactil" : undefined}
      type={tarjeta ? "default" : "text"}
      size={tarjeta ? "middle" : "small"}
      icon={<EyeOutlined />}
      disabled={!abrible}
      aria-label={`Abrir ${archivo.nombre}`}
      onClick={() => onAbrirArchivo(archivo)}
    >
      {tarjeta ? "Abrir" : null}
    </Button>
  );
  // En la tarjeta móvil el botón ya dice «Abrir»: un Tooltip ahí se quedaba pegado sobre el visor después
  // del toque (no hay «salir» del hover en una pantalla táctil). Solo se explica el motivo del deshabilitado.
  if (tarjeta && abrible) return <span className="alm-abrir-envoltorio">{boton}</span>;
  return (
    <Tooltip title={textoAccionAbrir(apertura)}>
      {/* Un botón deshabilitado no dispara el tooltip: el span sí. */}
      <span className={tarjeta ? "alm-abrir-envoltorio" : undefined}>{boton}</span>
    </Tooltip>
  );
};

/** Texto que abre el archivo con el clic del mouse (el teclado usa el botón «Abrir» o Enter en la fila). */
const AbribleTexto: React.FC<{
  abrible: boolean;
  onAbrir: () => void;
  children: React.ReactNode;
}> = ({ abrible, onAbrir, children }) =>
  abrible ? (
    <span className="alm-abrible alm-abrible-texto" onClick={onAbrir}>
      {children}
    </span>
  ) : (
    <>{children}</>
  );

const UsosCell: React.FC<{ archivo: Archivo }> = ({ archivo }) => {
  const n = archivo.usos.length;
  return n === 0 ? (
    <Text type="secondary">Sin uso</Text>
  ) : (
    <Text>
      {n} {n === 1 ? "lugar" : "lugares"}
    </Text>
  );
};

const ArchivosLista: React.FC<ArchivosListaProps> = ({
  archivos,
  cargando,
  esMovil,
  page,
  size,
  total,
  onPaginar,
  seleccion,
  onAlternar,
  orden,
  dir,
  onOrdenar,
  onEliminarUno,
  onAbrir,
  onAbrirArchivo,
}) => {
  const [abiertos, setAbiertos] = useState<ReadonlySet<string>>(new Set());
  const alternarAbierto = (id: string) =>
    setAbiertos((prev) => {
      const sig = new Set(prev);
      if (sig.has(id)) sig.delete(id);
      else sig.add(id);
      return sig;
    });

  const sortOrder = (campo: OrdenArchivos) =>
    orden === campo ? (dir === "asc" ? ("ascend" as const) : ("descend" as const)) : null;

  const columnas: ColumnsType<Archivo> = useMemo(
    () => [
      {
        title: "",
        key: "miniatura",
        width: 72,
        render: (_, a) => (
          <ArchivoMiniatura
            archivo={a}
            ampliable
            onAbrir={aperturaDeArchivo(a).modo === "ninguno" ? undefined : () => onAbrirArchivo(a)}
          />
        ),
      },
      {
        // Nombre + tipo en una celda de dos líneas: ahorra una columna a 1280 px.
        title: "Nombre",
        key: "nombre",
        dataIndex: "nombre",
        sorter: true,
        sortOrder: sortOrder("nombre"),
        sortDirections: ["ascend", "descend"],
        width: 180,
        render: (nombre: string, a) => (
          <div className="alm-celda-doble">
            <AbribleTexto
              abrible={aperturaDeArchivo(a).modo !== "ninguno"}
              onAbrir={() => onAbrirArchivo(a)}
            >
              <Text className="alm-nombre" ellipsis={{ tooltip: nombre }}>
                {nombre}
              </Text>
            </AbribleTexto>
            <span>
              <Tag style={{ marginInlineEnd: 0 }}>{etiquetaTipoArchivo(a)}</Tag>
            </span>
          </div>
        ),
      },
      {
        title: "Peso",
        key: "peso",
        dataIndex: "pesoBytes",
        sorter: true,
        sortOrder: sortOrder("peso"),
        sortDirections: ["descend", "ascend"],
        width: 84,
        align: "right",
        render: (_, a) => <PesoCell archivo={a} />,
      },
      {
        title: "Subido",
        key: "fecha",
        dataIndex: "fecha",
        sorter: true,
        sortOrder: sortOrder("fecha"),
        sortDirections: ["descend", "ascend"],
        width: 152,
        render: (_, a) => <FechaCell archivo={a} />,
      },
      {
        title: "Módulo y dueño",
        key: "modulo",
        sorter: true,
        sortOrder: sortOrder("modulo"),
        sortDirections: ["ascend", "descend"],
        width: 240,
        render: (_, a) => <DuenoCell archivo={a} />,
      },
      {
        title: "Quién lo subió",
        key: "subidoPor",
        width: 140,
        render: (_, a) => <SubidoPorCell subidoPor={a.subidoPor} />,
      },
      {
        // Estado + «usado en» juntos: la etiqueta arriba, la cantidad de lugares abajo.
        title: "Estado",
        key: "estado",
        dataIndex: "estado",
        width: 96,
        render: (_, a) => (
          <div className="alm-celda-doble">
            <span>
              <EstadoTag estado={a.estado} />
            </span>
            <UsosCell archivo={a} />
          </div>
        ),
      },
      {
        title: "",
        key: "acciones",
        width: 68,
        render: (_, a) => (
          <div className="alm-acciones-fila">
            <BotonAbrirArchivo
              archivo={a}
              apertura={aperturaDeArchivo(a)}
              onAbrirArchivo={onAbrirArchivo}
            />
            <Tooltip title="Eliminar este archivo">
              <Button
                danger
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                aria-label={`Eliminar ${a.nombre}`}
                onClick={() => onEliminarUno(a)}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orden, dir, onEliminarUno, onAbrirArchivo],
  );

  const config = paginacionServidor({
    page,
    size,
    total,
    etiqueta: ["archivo", "archivos"],
    onChange: onPaginar,
  });

  const vacio = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No hay archivos con estos filtros"
    />
  );

  // ── Móvil / tablet angosta: tarjetas ─────────────────────────────────────
  if (esMovil) {
    const todosMarcados = archivos.length > 0 && archivos.every((a) => seleccion.has(a.id));
    const algunoMarcado = archivos.some((a) => seleccion.has(a.id));
    if (cargando && archivos.length === 0) {
      return <Skeleton active paragraph={{ rows: 6 }} />;
    }
    return (
      <div className="alm-tarjetas" aria-busy={cargando}>
        {archivos.length === 0 ? (
          vacio
        ) : (
          <>
            <Checkbox
              className="alm-tarjetas-todos"
              checked={todosMarcados}
              indeterminate={!todosMarcados && algunoMarcado}
              onChange={(e) => onAlternar(archivos, e.target.checked)}
            >
              Seleccionar los {archivos.length} de esta página
            </Checkbox>
            {archivos.map((a) => {
              const marcado = seleccion.has(a.id);
              const abierto = abiertos.has(a.id);
              return (
                <article
                  key={a.id}
                  className={`alm-card${marcado ? " alm-card-sel" : ""}`}
                  aria-label={a.nombre}
                >
                  <div className="alm-card-fila">
                    <Checkbox
                      className="alm-card-check"
                      checked={marcado}
                      onChange={(e) => onAlternar([a], e.target.checked)}
                      aria-label={`Seleccionar ${a.nombre}`}
                    />
                    <ArchivoMiniatura
                      archivo={a}
                      size={64}
                      ampliable
                      onAbrir={
                        aperturaDeArchivo(a).modo === "ninguno" ? undefined : () => onAbrirArchivo(a)
                      }
                    />
                    <div className="alm-card-cuerpo">
                      <AbribleTexto
                        abrible={aperturaDeArchivo(a).modo !== "ninguno"}
                        onAbrir={() => onAbrirArchivo(a)}
                      >
                        <Text strong className="alm-card-nombre" ellipsis>
                          {a.nombre}
                        </Text>
                      </AbribleTexto>
                      <div className="alm-card-datos">
                        <Text strong>{formatPeso(a.pesoBytes)}</Text>
                        <FechaCell archivo={a} />
                        <EstadoTag estado={a.estado} />
                      </div>
                      <DuenoCell archivo={a} />
                    </div>
                  </div>
                  <div className="alm-card-acciones">
                    <Button
                      className="alm-btn-tactil"
                      icon={abierto ? <UpOutlined /> : <DownOutlined />}
                      onClick={() => alternarAbierto(a.id)}
                      aria-expanded={abierto}
                    >
                      {abierto ? "Ocultar detalles" : "Ver detalles"}
                    </Button>
                    <BotonAbrirArchivo
                      tarjeta
                      archivo={a}
                      apertura={aperturaDeArchivo(a)}
                      onAbrirArchivo={onAbrirArchivo}
                    />
                    <Button
                      className="alm-btn-tactil"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onEliminarUno(a)}
                      aria-label={`Eliminar ${a.nombre}`}
                    >
                      Eliminar
                    </Button>
                  </div>
                  {abierto && (
                    <div className="alm-card-detalle">
                      <div className="alm-card-quien">
                        <Text type="secondary">Subido por </Text>
                        <SubidoPorCell subidoPor={a.subidoPor} size={22} />
                      </div>
                      <ArchivoDetalle archivo={a} onAbrir={onAbrir} onAbrirArchivo={onAbrirArchivo} />
                    </div>
                  )}
                </article>
              );
            })}
            <Pagination
              className="alm-paginador-movil"
              size="small"
              showLessItems
              current={config.current}
              pageSize={config.pageSize}
              total={config.total}
              showSizeChanger={config.showSizeChanger}
              pageSizeOptions={config.pageSizeOptions}
              showTotal={config.showTotal}
              onChange={config.onChange}
            />
          </>
        )}
      </div>
    );
  }

  // ── Escritorio: tabla ────────────────────────────────────────────────────
  return (
    <Table<Archivo>
      className="alm-tabla"
      rowKey="id"
      size="middle"
      loading={cargando}
      dataSource={archivos}
      columns={columnas}
      locale={{ emptyText: vacio }}
      rowClassName={(a) => (seleccion.has(a.id) ? "alm-fila-sel" : "")}
      rowSelection={{
        selectedRowKeys: archivos.filter((a) => seleccion.has(a.id)).map((a) => a.id),
        onSelect: (fila, marcada) => onAlternar([fila], marcada),
        onSelectAll: (marcada, _filas, cambiadas) => onAlternar(cambiadas, marcada),
        columnWidth: COL_CASILLA,
      }}
      expandable={{
        columnWidth: COL_CASILLA,
        expandedRowRender: (a) => (
          <ArchivoDetalle archivo={a} onAbrir={onAbrir} onAbrirArchivo={onAbrirArchivo} />
        ),
      }}
      // Enter con la FILA enfocada abre el archivo. Si el foco está en un control de la fila (casilla,
      // «Eliminar», «Abrir», expandir) el evento lo atiende ese control: `target === currentTarget`
      // evita abrir dos veces o abrir al marcar una casilla con el teclado.
      onRow={(a) => ({
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key !== "Enter" || e.target !== e.currentTarget) return;
          if (aperturaDeArchivo(a).modo === "ninguno") return;
          e.preventDefault();
          onAbrirArchivo(a);
        },
      })}
      pagination={config as TablePaginationConfig}
      onChange={(_pag, _filtros, sorter, extra) => {
        if (extra.action !== "sort") return; // la paginación va por pagination.onChange
        const s = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<Archivo>;
        const campo = (s?.columnKey as OrdenArchivos | undefined) ?? undefined;
        if (!campo || !s.order) {
          // Tercer clic: vuelve al orden por defecto (lo más pesado primero).
          onOrdenar("peso", "desc");
          return;
        }
        onOrdenar(campo, s.order === "ascend" ? "asc" : "desc");
      }}
      // Layout fijo: sin él (auto) el navegador ensancha cada columna hasta su
      // contenido y los textos largos no se truncan (a 1280 px sobraban 41 px y el
      // botón de eliminar quedaba fuera). `scroll.x` = suma de los anchos de las
      // columnas + 2 casillas (ANCHO_TABLA): cabe sin desplazarse desde ese ancho de
      // contenedor; más angosto, desplaza dentro de la tarjeta.
      tableLayout="fixed"
      scroll={{ x: ANCHO_TABLA }}
    />
  );
};

export default ArchivosLista;
